import * as React from 'react';
import Button from '@ui-library/button';
import FormSchema from '@ui-library/formSchema';
import { Flex } from '@ui-library/misc';
import useStores from '@stores/useStores';
import TextInputField from '@ui-library/textInput/textInput';
import DynamicSelect, { MetaData } from '@ui-library/dynamicSelect';
import exportResponseError from '@utils/exportResponseError';
import Select from '@ui-library/select';
import Svg from '@svg/index';
import { CaretDownOutlined } from '@ant-design/icons';
import { CatchErr, FixMe } from '@utils/types';
import { ErrorBox } from '../styles';
import resourceConfig from './resourceConfig';
import { IResourceConfig } from '../../cloudSourceResources';

export type ResourcePayload = Omit<IResourceConfig, 'id'> & { id?: string };

interface Props {
  resources: string[];
  sourceRole: string;
  metadata: MetaData;
  normalResources?: string[];
  defaultValue?: Partial<IResourceConfig>;
  onSubmit?: (data: ResourcePayload) => void;
  onClose: () => void;
}

const CreateResourceModalForm = ({
  metadata,
  resources,
  defaultValue,
  sourceRole,
  normalResources = [],
  onSubmit,
  onClose,
}: Props) => {
  const [resource, setResource] = React.useState((defaultValue && defaultValue.role) || null);
  const [name, setName] = React.useState((defaultValue && defaultValue.name) || null);
  const [cloneCount, setCloneCount] = React.useState(0);
  const [names, setNames] = React.useState<string[] | null>(null);
  const [formConfig, setConfig] = React.useState({
    config: defaultValue && defaultValue.config,
    isValid: false,
  });
  const [loading, setLoading] = React.useState(false);
  const { messagesStore } = useStores();
  const onSubmitPromise = async () => {
    try {
      setLoading(true);

      if (onSubmit) {
        if (defaultValue?.id) {
          await onSubmit({
            id: defaultValue.id,
            role: resource!,
            sourceRole,
            name: name!,
            config: formConfig.config,
          });
        } else {
          await onSubmit({
            role: resource!,
            sourceRole,
            name: name!,
            config: formConfig.config,
          });
        }
      }
    } catch (err: CatchErr) {
      messagesStore.showErrorMessage(exportResponseError(err)?.message);
    } finally {
      setLoading(false);
    }
  };
  const config = resourceConfig[sourceRole as keyof typeof resourceConfig];
  const validName =
    name &&
    names &&
    (!names.includes(name) || name === defaultValue?.name) &&
    !normalResources?.includes(name);

  const isValid = formConfig.isValid && !!resource && !!validName;
  return (
    <div className="create_resource-modal_container">
      <Flex spaceBetween className="create_resource-modal_header">
        <div>Resource Details</div>
        <Svg name="close" onClick={onClose} />
      </Flex>
      <Flex flexDirection="column" centered className="create_resource-modal_body">
        <DynamicSelect
          field={
            {
              optionAsValue: true,
              method: 'get',
              label: 'Clone from',
              labelKey: 'name',
              url: `/cloudSources/roles/${sourceRole}/resources`,
            } as FixMe
          }
          onChange={(_, val: FixMe) => {
            if (val) {
              setResource(val.role);
              setConfig({ config: val.config, isValid: true });
              setCloneCount(cloneCount + 1);
            }
          }}
          onFetch={(data) => {
            setNames(data.map((rsource: { name: string }) => rsource.name));
          }}
          onError={(err) => messagesStore.showErrorMessage(err?.message || '')}
        />
        <div style={{ marginTop: 24 }} />
        <Select
          onChange={(val) => setResource(val)}
          label="Resource type"
          options={resources}
          value={resource}
          defaultValue={
            defaultValue && typeof defaultValue === 'object'
              ? defaultValue.role
              : !!defaultValue && defaultValue
          }
          suffixIcon={<CaretDownOutlined />}
        />
        <TextInputField
          type="input"
          field={{
            label: 'Name',
            required: true,
            default: defaultValue?.name,
          }}
          onChange={(_, val) => setName(val)}
        />
        <ErrorBox hasError={names && name && !validName}>The name should be unique</ErrorBox>
        {resource && config && (
          <div>
            <FormSchema
              key={cloneCount}
              metadata={metadata}
              initialSettings={formConfig.config || (defaultValue && defaultValue.config)}
              onChange={(data, isValid) => {
                setConfig({ config: data, isValid });
              }}
              schema={config[resource as keyof typeof config]}
            />
          </div>
        )}
      </Flex>
      <Flex flexDirection="column" centered className="create_resource-modal_footer">
        <Button
          disabled={!isValid}
          onClick={onSubmitPromise}
          loading={loading}
          style={{ marginTop: 24 }}
          name="Save"
        />
      </Flex>
    </div>
  );
};

export default CreateResourceModalForm;
